import React, { useState, useCallback, useRef, useEffect } from 'react';
import './App.css';
import './styles/RealProducts.css';
import { API_URL, ADVANCED_MODE, PHONE_CASE_MOCKUP_ENABLED, TSHIRT_3D_VIEWER_ENABLED } from './config';
import useSound from './hooks/useSound';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import PromptInput from './components/PromptInput';
import HowItWorks from './components/HowItWorks';
import GiftSection from './components/GiftSection';
import GeneratingState from './components/GeneratingState';
import ProductTabs from './components/ProductTabs';
import PhoneMockup from './components/PhoneMockup';
import TShirtViewer from './components/TShirtViewer';
import RealProducts from './components/RealProducts';
import Gallery from './components/Gallery';
import Footer from './components/Footer';
import Confetti from './components/Confetti';
import SettingsBar from "./components/SettingsBar";
import TrendingPrompts from './components/TrendingPrompts';
import StatsBar from './components/StatsBar';
import BackToTop from './components/BackToTop';
import ColorPalette from './components/ColorPalette';
import ProductCard from './components/ProductCard';
import LiveActivityToast from './components/LiveActivityToast';
import StyleRemix from './components/StyleRemix';
import GenerationHistory, { addToHistory } from './components/GenerationHistory';
import VariantCompare from './components/VariantCompare';
import ImageLightbox from './components/ImageLightbox';
import BookmarksPanel from './components/BookmarksPanel';
import { useBookmarks } from './hooks/useBookmarks';
import StyleDNA from './components/StyleDNA';
import VibeScore from './components/VibeScore';
import DesignTicker from './components/DesignTicker';
import RemixChain from './components/RemixChain';
import EvolutionTree from './components/EvolutionTree';
import GarmentColorPicker from './components/GarmentColorPicker';
import HoloCard from './components/HoloCard';
import RelatedDesigns from './components/RelatedDesigns';
import DesignCritique from './components/DesignCritique';
import ModelSwitch from './components/ModelSwitch';
import QualitySlider from './components/QualitySlider';

// ── URL state helpers ────────────────────────────────────────
function getDesignIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('design') ? parseInt(params.get('design')) : null;
}

function pushDesignToUrl(designId) {
  if (!designId) return;
  const url = new URL(window.location);
  url.searchParams.set('design', designId);
  window.history.pushState({ designId }, '', url);
}

// eslint-disable-next-line no-unused-vars
function clearDesignFromUrl() {
  const url = new URL(window.location);
  if (url.searchParams.has('design')) {
    url.searchParams.delete('design');
    window.history.pushState({}, '', url);
  }
}

// ── Theme helpers ────────────────────────────────────────────
const THEME_KEY = 'fashionai-theme';

function getInitialTheme() {
  try { return localStorage.getItem(THEME_KEY) || 'dark'; } catch { return 'dark'; }
}

function App() {
  const [theme, setTheme] = useState(getInitialTheme);
  const [uiMode, setUiMode] = useState(ADVANCED_MODE ? 'advanced' : 'simple');
  const [selectedModel, setSelectedModel] = useState("gpt-image-1"); // gpt-image-1 or nano-banana
  const [quality, setQuality] = useState("high"); // low | medium | high — lower = faster

  // Auto-select Real Products tab if it's the only available tab
  useEffect(() => {
    const phoneEnabled = PHONE_CASE_MOCKUP_ENABLED;
    const tshirtEnabled = TSHIRT_3D_VIEWER_ENABLED;
    const realEnabled = true;
    const totalEnabled = (phoneEnabled ? 1 : 0) + (tshirtEnabled ? 1 : 0) + (realEnabled ? 1 : 0);

    if (totalEnabled === 1 && realEnabled) {
      setActiveTab('real');
      console.log('📍 Auto-selected Real Products (only available tab on load)');
    }
  }, []);

  // Load and display version info on app start
  useEffect(() => {
    console.log('📥 Fetching version info from /version.json...');
    fetch('/version.json')
      .then(res => {
        console.log('📡 Version fetch response:', res.status, res.statusText);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(v => {
        console.log('');
        console.log('%c🚀 FashionAI Deployment Info', 'font-size: 14px; font-weight: bold; color: #00ffff;');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log(`📦 Commit: ${v.commit}`);
        console.log(`🔗 Full ID: ${v.commitFull}`);
        console.log(`💬 Message: ${v.commitMessage}`);
        console.log(`⏰ Deploy: ${v.deployTimeLocal}`);
        console.log(`🕐 UTC: ${v.deployTime}`);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('');
      })
      .catch(err => {
        console.warn('⚠️ Failed to load version info:', err.message);
      });
  }, []);

  // Apply theme to <html> data-theme attribute and persist
  useEffect(() => {
    document.body.setAttribute("data-ui-mode", uiMode);
  }, [uiMode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem(THEME_KEY, theme); } catch {}
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(t => t === 'dark' ? 'light' : 'dark');
  }, []);

  // 🔊 Sound
  const { muted: soundMuted, toggleMute: toggleSound, playChime } = useSound();

  // 🔖 Bookmarks + Collections
  const {
    bookmarks,
    toggleBookmark,
    isBookmarked,
    removeBookmark,
    clearAll: clearBookmarks,
    collections,
    addCollection,
    removeCollection,
    renameCollection,
    addToCollection,
    removeFromCollection,
  } = useBookmarks();
  const [showBookmarks, setShowBookmarks] = useState(false);

  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedVariants, setGeneratedVariants] = useState([]);
  const [originalImage, setOriginalImage] = useState(null); // Track original image for caching
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [currentDesignId, setCurrentDesignId] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const [activeTab, setActiveTab] = useState('phone');
  const [mockups, setMockups] = useState([]);
  const [productId, setProductId] = useState('');
  const [isOrdering, setIsOrdering] = useState(false);
  const [orderStep, setOrderStep] = useState(0);
  const [shareCopied, setShareCopied] = useState(false); // eslint-disable-line no-unused-vars
  const [isDownloading, setIsDownloading] = useState(false);
  const [variantFading, setVariantFading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  // Remix chain: tracks the creative journey when Style Remix chips are used
  const [remixChain, setRemixChain] = useState([]); // [{ prompt, imageUrl, styleLabel }]
  // Garment background color for the preview card
  const [previewBgColor, setPreviewBgColor] = useState('#FFFFFF');
  const pendingRemixLabelRef = useRef(null); // label of the style chip being remixed
  const promptInputRef = useRef(null);
  const resultRef = useRef(null);
  const prevIsGeneratingRef = useRef(false);

  // Load a single design by ID into state
  const loadDesignById = useCallback(async (id, { push = true } = {}) => {
    try {
      const res = await fetch(`${API_URL}/designs/${id}`);
      if (!res.ok) return;
      const { design } = await res.json();
      if (!design) return;

      setGeneratedImage(design.image_url);
      setOriginalImage(design.image_url);
      setGeneratedVariants([design.image_url]);
      setCurrentPrompt(design.prompt);
      setCurrentDesignId(design.id);
      setMockups([]);
      setProductId('');

      if (promptInputRef.current) {
        promptInputRef.current.setPrompt(design.prompt);
      }

      if (push) pushDesignToUrl(design.id);
      console.log(`✅ Loaded design ${design.id} from URL`);
    } catch (err) {
      console.warn('Could not load design from URL:', err);
    }
  }, []);

  // On mount: load design from ?design=ID if present
  useEffect(() => {
    const urlDesignId = getDesignIdFromUrl();
    if (urlDesignId) {
      loadDesignById(urlDesignId, { push: false });
    }
  }, [loadDesignById]);

  // Handle browser back/forward
  useEffect(() => {
    const onPopState = (e) => {
      const id = e.state?.designId || getDesignIdFromUrl();
      if (id) {
        loadDesignById(id, { push: false });
      } else {
        // Back to home — clear design
        setGeneratedImage(null);
        setGeneratedVariants([]);
        setCurrentPrompt('');
        setCurrentDesignId(null);
        setMockups([]);
        setProductId('');
        if (promptInputRef.current) promptInputRef.current.setPrompt('');
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [loadDesignById]);

  // Cycle through mockup steps while ordering
  useEffect(() => {
    document.body.setAttribute("data-ui-mode", uiMode);
  }, [uiMode]);

  useEffect(() => {
    if (!isOrdering) { setOrderStep(0); return; }
    const interval = setInterval(() => {
      setOrderStep(s => (s + 1) % 3);
    }, 1800);
    return () => clearInterval(interval);
  }, [isOrdering]);


  // Load last gallery images on mount (skip if URL has a specific design)
  useEffect(() => {
    if (getDesignIdFromUrl()) return; // design loaded from URL instead

    const loadLastGalleryImages = async () => {
      try {
        const res = await fetch(`${API_URL}/designs?limit=3&sort=recent`);
        const data = await res.json();
        if (data.designs && data.designs.length > 0) {
          // Filter out placeholder images
          const validDesigns = data.designs.filter(d => 
            d.image_url && 
            !d.image_url.includes("picsum") &&
            (d.image_url.startsWith('https://fashionai-api') || d.image_url.startsWith('data:'))
          );
          
          if (validDesigns.length > 0) {
            const latest = validDesigns[0];
            const urls = validDesigns.map(d => d.image_url);

            setGeneratedImage(latest.image_url);
            setCurrentPrompt(latest.prompt);
            setCurrentDesignId(latest.id);
            setGeneratedVariants(urls);
            if (latest.id) pushDesignToUrl(latest.id);

            // Also set the prompt in the input field
            if (promptInputRef.current) {
              promptInputRef.current.setPrompt(latest.prompt);
            }

            console.log(`✅ Loaded ${validDesigns.length} recent design(s) from gallery`);
          }
        }
      } catch (err) {
        console.log("Could not load gallery images:", err);
      }
    };
    
    loadLastGalleryImages();
  }, []);

  // ============================================================
  // AI IMAGE GENERATION — PLACEHOLDER / DEMO FLOW
  // 
  // TODO: Replace this placeholder with a real AI image generation API.
  // Options:
  //   - OpenAI DALL-E 3: POST https://api.openai.com/v1/images/generations
  //   - Stability AI: POST https://api.stability.ai/v1/generation/...
  //   - Midjourney API, Replicate, etc.
  //
  // The real integration would:
  //   1. Send the prompt to the AI API
  //   2. Receive back a generated image URL or base64
  //   3. Set that as the generatedImage state
  //
  // For now, we simulate with picsum.photos placeholder images.
  // ============================================================
  const handleGenerate = useCallback(async (prompt) => {
    console.log('');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🎨 IMAGE GENERATION STARTED');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🔗 API Endpoint:', `${API_URL}/generate`);
    console.log('📤 Request Method: POST');
    console.log('📝 Prompt:', prompt);
    console.log('🤖 Model:', selectedModel);

    // Capture current image + prompt for the chain BEFORE clearing state
    const isRemix = pendingRemixLabelRef.current !== null;
    const chainEntry = isRemix
      ? { prompt: currentPrompt, imageUrl: generatedImage, styleLabel: pendingRemixLabelRef.current }
      : null;
    pendingRemixLabelRef.current = null; // consume

    setIsGenerating(true);
    setGeneratedImage(null);
    setGeneratedVariants([]);
    setGenerateError(null);
    setCurrentPrompt(prompt);
    setCurrentDesignId(null);  // Reset design ID for new generation
    setMockups([]);
    setProductId('');
    setVariantFading(false);

    // Update remix chain
    if (isRemix && chainEntry) {
      setRemixChain(prev => {
        const base = prev.length === 0
          ? [chainEntry]
          : [...prev, chainEntry];
        return base;
      });
    } else {
      // Fresh generation — clear the chain
      setRemixChain([]);
    }

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 240000); // 240s timeout (high-quality gpt-image-2 can take >3min)

      const requestBody = { prompt, model: selectedModel, quality };
      console.log('📋 Request Body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(`${API_URL}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      clearTimeout(timeout);
      console.log('📡 Response received. Status:', response.status);

      const data = await response.json();
      console.log('📊 Response Data:');
      console.log('  URLs:', data.urls);
      console.log('  Design ID:', data.design_id);
      console.log('  Error:', data.error);

      if (!response.ok || (!data.url && !data.urls?.length)) {
        throw new Error(data.error || 'Image generation failed');
      }

      const urls = data.urls || [data.url];
      setGeneratedVariants(urls);
      setGeneratedImage(urls[0]);
      setOriginalImage(urls[0]); // Store original image for variant caching
      setCurrentDesignId(data.design_id || null);  // Store design_id for caching
      if (data.design_id) pushDesignToUrl(data.design_id);

      // Save to local generation history
      addToHistory({ prompt, imageUrl: urls[0], variants: urls, designId: data.design_id || null });

      // Append the freshly generated result as the "current" step in the remix chain
      if (isRemix) {
        setRemixChain(prev => [
          ...prev,
          { prompt, imageUrl: urls[0], styleLabel: null /* current = last */ },
        ]);
      }

      // Auto-select Real Products if it's the only available tab
      const phoneEnabled = PHONE_CASE_MOCKUP_ENABLED;
      const tshirtEnabled = TSHIRT_3D_VIEWER_ENABLED;
      const realEnabled = true; // Real Products always available
      const totalEnabled = (phoneEnabled ? 1 : 0) + (tshirtEnabled ? 1 : 0) + (realEnabled ? 1 : 0);

      if (totalEnabled === 1 && realEnabled) {
        setActiveTab('real');
        console.log('📍 Auto-selected Real Products (only available tab)');
      }

      // 🎉 Celebrate!
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4000);
      // 🔊 Play sparkle chime
      playChime();
    } catch (error) {
      console.error('Generation failed:', error);
      setGenerateError(error.name === 'AbortError' ? 'Generation timed out — please try again.' : `Error: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  }, [selectedModel, quality]);

  // Auto-scroll to result when generation completes
  useEffect(() => {
    const wasGenerating = prevIsGeneratingRef.current;
    prevIsGeneratingRef.current = isGenerating;

    // Transition: was generating → done generating → image available
    if (wasGenerating && !isGenerating && generatedImage && resultRef.current) {
      // Short delay to let the fade-in animation start first
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [isGenerating, generatedImage]);

  // Variant switch with cross-fade animation
  const handleVariantSwitch = useCallback((url) => {
    if (url === generatedImage) return;
    const variantIndex = generatedVariants.indexOf(url);
    const variantNames = ['🔥 Bold', '✨ Minimal', '🌑 Dark'];
    const variantName = variantNames[variantIndex] || `Variant ${variantIndex + 1}`;
    console.log(`\n🎨 VARIANT SELECTED: ${variantName}`);
    console.log(`📍 Image URL: ${url}`);
    setVariantFading(true);
    setTimeout(() => {
      setGeneratedImage(url);
      setVariantFading(false);
    }, 200); // matches CSS transition duration
  }, [generatedImage, generatedVariants]);

  // Download generated design as PNG
  const handleDownload = useCallback(async () => { // eslint-disable-line no-unused-vars
    if (!generatedImage || isDownloading) return;
    setIsDownloading(true);
    try {
      // Try proxied fetch first (CORS-safe), fall back to direct link
      const proxyUrl = `${API_URL}/proxy-image?url=${encodeURIComponent(generatedImage)}`;
      let blob;
      try {
        const res = await fetch(proxyUrl);
        if (!res.ok) throw new Error('proxy failed');
        blob = await res.blob();
      } catch {
        // Direct fetch as fallback
        const res = await fetch(generatedImage);
        blob = await res.blob();
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = (currentPrompt || 'fashionai-design')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .slice(0, 40);
      a.href = objectUrl;
      a.download = `${slug}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (err) {
      console.error('Download failed:', err);
      // Last-resort: open in new tab
      window.open(generatedImage, '_blank');
    } finally {
      setIsDownloading(false);
    }
  }, [generatedImage, currentPrompt, isDownloading]);

  // Share design — Web Share API with clipboard fallback
  const handleShare = useCallback(async () => { // eslint-disable-line no-unused-vars
    // Build shareable URL with ?design= param
    const shareUrl = currentDesignId
      ? `${window.location.origin}?design=${currentDesignId}`
      : window.location.href;

    const shareData = {
      title: 'FashionAI — AI-Generated Design',
      text: currentPrompt ? `Check out this AI design: "${currentPrompt}"` : 'Check out this AI-generated fashion design!',
      url: shareUrl,
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Share failed:', err);
      }
    } else {
      // Fallback: copy shareable URL to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch (err) {
        console.error('Clipboard copy failed:', err);
      }
    }
  }, [currentPrompt, currentDesignId]);

  // Printify mockup flow — sends image URL to backend
  const handleOrder = useCallback(async () => { // eslint-disable-line no-unused-vars
    if (!generatedImage || isOrdering) return;
    setIsOrdering(true);
    setMockups([]);

    try {
      const response = await fetch(`${API_URL}/mockups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          image_url: generatedImage,
          design_id: currentDesignId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Mockup generation failed: ${response.status}`);
      }

      const result = await response.json();

      if (result.cached) {
        console.log('✅ Using cached mockups');
      } else {
        console.log('✨ Generated new mockups');
      }

      // Handle both old and new response formats
      const mockupImages = result.mockups?.map((m) => ({
        src: m.images[0]?.src,
        type: m.type,
      })) || result.images?.map((image) => image.src) || [];

      setMockups(mockupImages);
      setProductId(result.mockups?.[0]?.product_id || result.id);
    } catch (error) {
      console.error('Mockup generation failed:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsOrdering(false);
    }
  }, [generatedImage, currentDesignId, isOrdering]);

  // Publish to store and redirect
  const handleVisitStore = useCallback(async () => {
    if (!productId) return;

    try {
      const response = await fetch(`${API_URL}/publish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: productId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Publish failed: ${response.status}`);
      }

      const result = await response.json();
      if (!result.external?.handle) {
        throw new Error('No store URL returned from server');
      }

      console.log('✅ Redirecting to:', result.external.handle);
      window.location.href = result.external.handle;
    } catch (error) {
      console.error('Publish failed:', error);
      alert(`Failed to open store: ${error.message}`);
    }
  }, [productId]);

  return (
    <div className="App">
      <Navbar
        theme={theme}
        onToggleTheme={toggleTheme}
        soundMuted={soundMuted}
        onToggleSound={toggleSound}
        bookmarkCount={bookmarks.length}
        onOpenBookmarks={() => setShowBookmarks(true)}
      />
      <Hero onRoulette={(design) => {
        // Load the randomly picked design into the creator
        setGeneratedImage(design.image_url);
        setOriginalImage(design.image_url);
        setGeneratedVariants([design.image_url]);
        setCurrentPrompt(design.prompt || '');
        setCurrentDesignId(design.id || null);
        setMockups([]);
        setProductId('');
        if (design.id) pushDesignToUrl(design.id);
        if (promptInputRef.current) promptInputRef.current.setPrompt(design.prompt || '');
        // Scroll to create section
        setTimeout(() => {
          document.getElementById('create')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }} />
      <DesignTicker onSelectDesign={(d) => {
        // Load the selected design into the creator
        setGeneratedImage(d.image_url);
        setOriginalImage(d.image_url);
        setGeneratedVariants([d.image_url]);
        setCurrentPrompt(d.prompt || '');
        setCurrentDesignId(d.id || null);
        setMockups([]);
        setProductId('');
        if (d.id) pushDesignToUrl(d.id);
        if (promptInputRef.current) promptInputRef.current.setPrompt(d.prompt || '');
        // Smooth scroll to create section
        setTimeout(() => {
          document.getElementById('create')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }} />
      <TrendingPrompts onSelectPrompt={(prompt) => {
        promptInputRef.current?.setPrompt(prompt);
        // Smooth scroll to create section
        document.getElementById('create')?.scrollIntoView({ behavior: 'smooth' });
      }} />
      <StatsBar />
      {/* SettingsBar - Only show in advanced mode */}
      {ADVANCED_MODE && <SettingsBar uiMode={uiMode} setUiMode={setUiMode} selectedModel={selectedModel} setSelectedModel={setSelectedModel} />}

      {/* ── Create Section: prompt + preview side by side ── */}
      <section className="create-section" id="create">
        {/* Left: input + controls */}
        <div className="create-left">
          <h2 className="create-title"><span className="step-badge-inline">1</span> Create Your Design</h2>
          <p className="create-subtitle">Describe anything — AI does the rest.</p>
          <PromptInput ref={promptInputRef} onGenerate={handleGenerate} isLoading={isGenerating} />

          {/* Model picker: ChatGPT (OpenAI) vs Nano Banana (Gemini) */}
          <ModelSwitch
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            disabled={isGenerating}
          />

          {/* Quality slider: lower = faster inference.
              Applies to ChatGPT (OpenAI quality) and Nano Banana Pro (Gemini imageSize).
              Plain Nano Banana (Flash) has no quality lever, so the slider is hidden. */}
          {selectedModel !== 'nano-banana' && (
            <QualitySlider
              quality={quality}
              setQuality={setQuality}
              disabled={isGenerating}
            />
          )}

          {/* Variant selector (shown once image is ready) */}
          {generatedVariants.length > 1 && generatedImage && (
            <>
              <h2 className="create-title"><span className="step-badge-inline">2</span> Select your variant</h2>
              <div className="left-variant-selector fade-in">
                <div className="variant-selector-row">
                {generatedVariants.map((url, i) => (
                  <button
                    key={i}
                    className={`left-variant-thumb ${generatedImage === url ? 'active' : ''}`}
                    onClick={() => handleVariantSwitch(url)}
                    title={['🔥 Bold', '✨ Minimal', '🌑 Dark'][i]}
                  >
                    <img src={url} alt={`Variant ${i + 1}`} />
                    <span>{['Bold', 'Minimal', 'Dark'][i]}</span>
                  </button>
                ))}
                </div>
              </div>
            </>
          )}

          {/* Powered-by badge — reflects the selected model */}
          <div className="dalle-badge">
            <span className="dalle-badge-dot" />
            {selectedModel === 'nano-banana-pro'
              ? 'Powered by Nano Banana Pro (Gemini)'
              : selectedModel === 'nano-banana'
              ? 'Powered by Nano Banana (Gemini Flash)'
              : 'Powered by GPT Image (OpenAI)'}
          </div>

          {/* Error */}
          {generateError && !isGenerating && (
            <div className="error-container fade-in">
              <p className="error-text">⚠️ {generateError}</p>
            </div>
          )}

          {/* Order button (shown once image is ready) */}
          {generatedImage && !isGenerating && (
            <div className="create-actions fade-in">
              <ProductTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                phoneEnabled={PHONE_CASE_MOCKUP_ENABLED}
                tshirtEnabled={TSHIRT_3D_VIEWER_ENABLED}
              />
            </div>
          )}

          {/* Mockup results */}
          {mockups.length > 0 && (
            <div className="mockup-results-inline fade-in">
              <h4>Product Mockups</h4>
              <div className="mockup-grid">
                {mockups.map((src, index) => (
                  <img key={index} src={src} alt={`Mockup ${index + 1}`} />
                ))}
              </div>
              {productId && (
                <button className="visit-store-btn" onClick={handleVisitStore}>
                  🏪 Visit Store & Purchase
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right: live preview */}
        <div className="create-right">
          {/* Empty state */}
          {!isGenerating && !generatedImage && (
            <div className="preview-empty">
              <div className="preview-empty-icon">🎨</div>
              <p>Your design will appear here</p>
              <span>Type a prompt and hit Generate</span>
            </div>
          )}

          {/* Loading */}
          {isGenerating && (
            <GeneratingState prompt={currentPrompt} />
          )}

          {/* Preview */}
          {generatedImage && !isGenerating && (
            <div ref={resultRef} className="fade-in" style={{width:'100%', position:'relative'}}>
              {/* Variant thumbnails + Regenerate */}
              <div className="variant-strip-row">
                {generatedVariants.length > 1 && (
                  <div className="variant-strip">
                    {generatedVariants.map((url, i) => (
                      <button
                        key={i}
                        className={`variant-thumb ${generatedImage === url ? 'active' : ''}`}
                        onClick={() => handleVariantSwitch(url)}
                        data-tooltip={['🔥 Bold', '✨ Minimal', '🌑 Dark'][i]}
                        aria-label={['Bold variant', 'Minimal variant', 'Dark variant'][i]}
                      >
                        <img src={url} alt={`Variant ${i + 1}`} />
                        <span>{['🔥 Bold', '✨ Minimal', '🌑 Dark'][i]}</span>
                      </button>
                    ))}
                  </div>
                )}
                {currentPrompt && (
                  <button
                    className="regenerate-btn"
                    onClick={() => handleGenerate(currentPrompt)}
                    title="Generate 3 new variants with the same prompt"
                  >
                    🔄 Regenerate
                  </button>
                )}
              </div>
              <div className={`variant-preview-wrap${variantFading ? ' variant-fading' : ''}`}>
                {activeTab === 'real' && generatedImage && (
                  <HoloCard maxTilt={14}>
                    <div
                      className="generated-image-container"
                      style={{
                        background: previewBgColor,
                        transition: 'background 0.35s ease',
                        borderRadius: '12px',
                      }}
                    >
                      <img src={generatedImage} alt="Generated Design" className="generated-image-preview" />
                      <div className="ai-watermark-badge">
                        <span className="ai-watermark-icon">✨</span>
                        <span className="ai-watermark-text">Made with FashionAI</span>
                      </div>
                      <ImageLightbox imageUrl={generatedImage} prompt={currentPrompt} />
                    </div>
                  </HoloCard>
                )}
                {activeTab === 'real' && generatedImage && (
                  <GarmentColorPicker selected={previewBgColor} onChange={setPreviewBgColor} />
                )}
                {activeTab === 'phone' && PHONE_CASE_MOCKUP_ENABLED && <PhoneMockup imageUrl={generatedImage} />}
                {activeTab === 'tshirt' && TSHIRT_3D_VIEWER_ENABLED && <TShirtViewer imageUrl={generatedImage} />}
              </div>

              {/* Bookmark button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
                <button
                  className={`bookmark-btn${isBookmarked({ imageUrl: generatedImage, designId: currentDesignId }) ? ' bookmarked' : ''}`}
                  onClick={() => toggleBookmark({ imageUrl: generatedImage, prompt: currentPrompt, designId: currentDesignId })}
                  title={isBookmarked({ imageUrl: generatedImage, designId: currentDesignId }) ? 'Remove bookmark' : 'Save to bookmarks'}
                >
                  <span className="bm-icon">
                    {isBookmarked({ imageUrl: generatedImage, designId: currentDesignId }) ? '🔖' : '🔖'}
                  </span>
                  {isBookmarked({ imageUrl: generatedImage, designId: currentDesignId }) ? 'Bookmarked' : 'Bookmark'}
                </button>
              </div>

              {/* Variant Compare + Style Remix row */}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                {generatedVariants.length >= 2 && (
                  <VariantCompare variants={generatedVariants} />
                )}
                {!isOrdering && currentPrompt && (
                  <StyleRemix
                    prompt={currentPrompt}
                    onRemix={(remixPrompt, styleLabel) => {
                      pendingRemixLabelRef.current = styleLabel || null;
                      handleGenerate(remixPrompt);
                    }}
                    isLoading={isGenerating}
                  />
                )}
              </div>

              {/* Evolution Tree — animated branching diagram of the remix lineage */}
              {!isOrdering && remixChain.length >= 2 && (
                <EvolutionTree
                  chain={remixChain}
                  isGenerating={isGenerating}
                  onNavigate={(index) => {
                    const step = remixChain[index];
                    if (!step) return;
                    setGeneratedImage(step.imageUrl);
                    setOriginalImage(step.imageUrl);
                    setGeneratedVariants([step.imageUrl]);
                    setCurrentPrompt(step.prompt);
                    if (promptInputRef.current) promptInputRef.current.setPrompt(step.prompt || '');
                    // Trim the chain to this index + 1
                    setRemixChain(prev => prev.slice(0, index + 1));
                  }}
                  onClear={() => setRemixChain([])}
                />
              )}

              {/* Remix Chain — compact breadcrumb trail (shown alongside the tree for quick nav) */}
              {!isOrdering && remixChain.length >= 2 && (
                <RemixChain
                  chain={remixChain}
                  onNavigate={(index) => {
                    const step = remixChain[index];
                    if (!step) return;
                    setGeneratedImage(step.imageUrl);
                    setOriginalImage(step.imageUrl);
                    setGeneratedVariants([step.imageUrl]);
                    setCurrentPrompt(step.prompt);
                    if (promptInputRef.current) promptInputRef.current.setPrompt(step.prompt || '');
                    // Trim the chain to this index + 1
                    setRemixChain(prev => prev.slice(0, index + 1));
                  }}
                  onClear={() => setRemixChain([])}
                />
              )}

              {/* Color Palette */}
              {!isOrdering && (
                <ColorPalette imageUrl={generatedImage} />
              )}

              {/* Style DNA */}
              {!isOrdering && (
                <StyleDNA imageUrl={generatedImage} />
              )}

              {/* Vibe Score */}
              {!isOrdering && currentPrompt && (
                <VibeScore prompt={currentPrompt} />
              )}

              {/* AI Design Critique */}
              {!isOrdering && generatedImage && currentPrompt && (
                <DesignCritique imageUrl={generatedImage} prompt={currentPrompt} />
              )}

              {/* Related Designs */}
              {!isOrdering && generatedImage && currentPrompt && (
                <RelatedDesigns
                  currentPrompt={currentPrompt}
                  currentDesignId={currentDesignId}
                  onUsePrompt={(prompt) => {
                    setCurrentPrompt(prompt);
                    if (promptInputRef.current) promptInputRef.current.setPrompt(prompt);
                  }}
                />
              )}

              {/* Save as Card */}
              {!isOrdering && (
                <div className="product-card-row fade-in">
                  <ProductCard imageUrl={generatedImage} prompt={currentPrompt} />
                </div>
              )}

              {/* Mockup Generating Overlay */}
              {isOrdering && (
                <div className="mockup-generating-overlay fade-in">
                  <div className="mockup-generating-inner">
                    <div className="mockup-spinner" />
                    <p className="mockup-generating-title">Creating your mockups…</p>
                    <p className="mockup-generating-sub">Placing your design on products</p>
                    <div className="mockup-generating-steps">
                      <span className={`mockup-step ${orderStep === 0 ? 'active' : ''}`}>🎨 Applying design</span>
                      <span className="mockup-step-divider">→</span>
                      <span className={`mockup-step ${orderStep === 1 ? 'active' : ''}`}>👕 Rendering products</span>
                      <span className="mockup-step-divider">→</span>
                      <span className={`mockup-step ${orderStep === 2 ? 'active' : ''}`}>✨ Finishing up</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Real Products Section */}
      {generatedImage && activeTab === 'real' && (
        <section className="real-products-section" id="real-products-section">
          <div className="real-products-header">
            <h2><span className="step-badge-inline">3</span> Shop Your Design</h2>
          </div>
          <RealProducts imageUrl={generatedImage} designId={currentDesignId} originalImage={originalImage} />
        </section>
      )}

      <HowItWorks />
      <GiftSection />
      <Gallery
        onToggleBookmark={toggleBookmark}
        isBookmarked={isBookmarked}
        onUsePrompt={(prompt, imageUrl, designId) => {
        // Set the prompt in the input
        promptInputRef.current?.setPrompt(prompt);

        // Load the image into the viewers
        if (imageUrl) {
          setGeneratedImage(imageUrl);
          setOriginalImage(imageUrl); // Set original image for variant caching
          setGeneratedVariants([imageUrl]);
          setCurrentPrompt(prompt);
          setCurrentDesignId(designId);  // Store design ID for caching
          if (designId) pushDesignToUrl(designId);
        }
      }}
      />
      <Footer />
      <Confetti active={showConfetti} duration={3500} />
      <BackToTop />
      <LiveActivityToast />
      <GenerationHistory
        onSelect={(entry) => {
          setGeneratedImage(entry.imageUrl);
          setOriginalImage(entry.imageUrl);
          setGeneratedVariants(entry.variants?.length ? entry.variants : [entry.imageUrl]);
          setCurrentPrompt(entry.prompt);
          setCurrentDesignId(entry.designId || null);
          setMockups([]);
          setProductId('');
          if (entry.designId) pushDesignToUrl(entry.designId);
          if (promptInputRef.current) promptInputRef.current.setPrompt(entry.prompt);
          // Scroll to create section
          setTimeout(() => {
            document.getElementById('create')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 200);
        }}
      />

      {/* 🔖 Bookmarks Panel */}
      {showBookmarks && (
        <BookmarksPanel
          bookmarks={bookmarks}
          onClose={() => setShowBookmarks(false)}
          onRemove={removeBookmark}
          onClearAll={clearBookmarks}
          collections={collections}
          onAddToCollection={addToCollection}
          onRemoveFromCollection={removeFromCollection}
          onCreateCollection={addCollection}
          onDeleteCollection={removeCollection}
          onRenameCollection={renameCollection}
          onSelect={(bm) => {
            setGeneratedImage(bm.imageUrl);
            setOriginalImage(bm.imageUrl);
            setGeneratedVariants([bm.imageUrl]);
            setCurrentPrompt(bm.prompt || '');
            setCurrentDesignId(bm.designId || null);
            setMockups([]);
            setProductId('');
            if (bm.designId) pushDesignToUrl(bm.designId);
            if (promptInputRef.current) promptInputRef.current.setPrompt(bm.prompt || '');
            setTimeout(() => {
              document.getElementById('create')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 200);
          }}
        />
      )}
    </div>
  );
}

export default App;
